import { useContext, useEffect, useState } from 'react';
import ModalComponent from 'components/Common/Modal';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import useCrmInteraction from 'api/hooks/useCrmInteraction';
import useCrmEmails from 'api/hooks/useCrmEmails';
import useCrmContacts from 'api/hooks/useCrmContacts';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import dayjs from 'dayjs';
import { store } from 'components/Store';
import { useRef } from 'react';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import InteractionFormFields, {
  InteractionFormFieldsSkeleton,
  DEFAULT_INTERACTION_TITLE,
} from '../InteractionFormFields';
import normalizeInteractionHtmlBody from '../normalizeInteractionHtmlBody';
import { stripEmailAttachmentControls } from 'components/Dashboard/Influence/CrmEmails/buildEmailAttachmentHtml';

const LogInteractionModal = ({
  onClose,
  onSuccess,
  title: modalTitle,
  preselectedStakeholders = [],
  stakeholdersFilterQuery,
  interactionId,
}) => {
  const isEditing = !!interactionId;
  const { state } = useContext(store);
  const { agencyUser, newAgencyPlan } = useTopicOrClientWord();
  const defaultTitle =
    agencyUser || newAgencyPlan ? `Log an interaction for ${state.team?.teamName}` : 'Log an interaction';
  const { myself } = state.team ?? {};
  const [isLoading, setIsLoading] = useState(isEditing || !!stakeholdersFilterQuery);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: DEFAULT_INTERACTION_TITLE,
    date: dayjs().format('YYYY-MM-DD'),
    type: '',
    description: '',
    attachments: [],
    tags: [],
    initiator: 'Team',
    stakeholders: preselectedStakeholders,
    members: myself ? [{ value: myself.id, id: myself.id, label: myself.fullName ?? myself.emailAddress }] : [],
    sourceCrmEmailId: null,
  });
  const [recipientsSendRecords, setRecipientsSendRecords] = useState(null);
  const [isLoadingRecipients, setIsLoadingRecipients] = useState(false);
  const [senderEmailAddress, setSenderEmailAddress] = useState(null);
  const [emailSubject, setEmailSubject] = useState(null);

  const { getCrmInteraction, addCrmInteraction, saveCrmInteraction } = useCrmInteraction();
  const { getCrmEmail } = useCrmEmails();
  const { queryCrmContacts } = useCrmContacts();

  // Clicking the overlay while a field's dropdown is open should close just that dropdown, not the
  // form underneath it - clicking it with nothing open should close the form as normal. Snapshotted
  // into a ref at mousedown time (before the dropdown's own close reaches state) since by the time
  // the resulting click/request-close fires, openDropdown has often already flipped back to null and
  // the distinction would be lost.
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownWasOpenRef = useRef(false);

  const initialFormRef = useRef(null);
  const [isBaselineReady, setIsBaselineReady] = useState(!isEditing);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const baselineCapturedRef = useRef(false);
  const loadInteractionData = useRef();
  loadInteractionData.current = async (source) => {
    try {
      const result = await getCrmInteraction({ id: interactionId, signal: source.signal });
      if (result) {
        const loadedForm = {
          title: result.title ?? DEFAULT_INTERACTION_TITLE,
          date: result.dateTime ? dayjs(result.dateTime).format('YYYY-MM-DD') : '',
          type: result.interactionType ?? '',
          description: normalizeInteractionHtmlBody(result),
          attachments: result.attachments ?? [],
          tags: (result.tags ?? []).map((tag) => ({ label: tag, value: tag })),
          initiator:
            result.initiator ??
            (result.initiatedByOrganisation === true
              ? 'Team'
              : result.initiatedByOrganisation === false
                ? 'Stakeholder'
                : ''),
          stakeholders: (result.stakeholderContacts ?? []).map((c) => ({
            value: c.id,
            label: c.fullName ?? '',
            id: c.id,
            email: '',
            imageId: c.imageId ?? null,
            imageUrl: null,
            stakeholderContactId: c.stakeholderContactId ?? null,
            party: c.party ?? null,
          })),
          members: (result.associatedUserIds ?? []).map((id, idx) => {
            const nameFromResponse = result.associatedUserFullNames?.[idx];
            const member = !nameFromResponse ? state.team?.allMembers?.find((m) => m.id === id) : null;
            return { value: id, label: nameFromResponse ?? member?.fullName ?? member?.emailAddress ?? `User ${id}` };
          }),
          sourceCrmEmailId: result.sourceCrmEmailId ?? null,
        };
        setForm(loadedForm);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isEditing) return;
    initialFormRef.current = null;
    baselineCapturedRef.current = false;
    setIsBaselineReady(false);
    setIsEditorReady(false);
    const source = new AbortController();
    loadInteractionData.current(source);
    return () => source.abort('Interaction edit form cancelled');
  }, [interactionId]);

  useEffect(() => {
    if (!isEditing || isLoading || !isEditorReady || baselineCapturedRef.current) return;
    baselineCapturedRef.current = true;
    const t = setTimeout(() => {
      initialFormRef.current = form;
      setIsBaselineReady(true);
    }, 400);
    return () => clearTimeout(t);
  }, [form, isEditing, isLoading, isEditorReady]);

  useEffect(() => {
    if (!form.sourceCrmEmailId) return;
    const source = new AbortController();
    setIsLoadingRecipients(true);
    (async () => {
      try {
        const crmEmailModel = await getCrmEmail({ id: form.sourceCrmEmailId, signal: source.signal });
        setRecipientsSendRecords(crmEmailModel.sendRecords ?? []);
        setSenderEmailAddress(crmEmailModel.senderEmailAddress ?? null);
        setEmailSubject(crmEmailModel.subject ?? null);
      } catch (error) {}
      setIsLoadingRecipients(false);
    })();
    return () => source.abort('Interaction recipients fetch cancelled');
  }, [form.sourceCrmEmailId]);

  // "Select all" on the Directory doesn't carry actual contact records (there can be thousands,
  // well beyond what's loaded client-side) - just the filters/search that produced that selection.
  // Resolve them here the same way the query itself would, same endpoint the contacts list uses.
  const loadStakeholdersByQuery = useRef();
  loadStakeholdersByQuery.current = async (source) => {
    try {
      const result = await queryCrmContacts({
        filters: stakeholdersFilterQuery.filters ?? [],
        query: stakeholdersFilterQuery.query ?? '',
        pageSize: 10000,
        pageNumber: 1,
        signal: source.signal,
      });
      if (result) {
        const stakeholders = (result.hits ?? []).map((c) => ({
          value: c.id,
          id: c.id,
          label: c.title,
          email: c.email,
          imageId: c.imageId ?? null,
          imageUrl: c.imageUrl ?? null,
          stakeholderContactId: c.contactId ?? null,
          party: c.party ?? null,
        }));
        setForm((f) => ({ ...f, stakeholders }));
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isEditing || !stakeholdersFilterQuery) return;
    const source = new AbortController();
    loadStakeholdersByQuery.current(source);
    return () => source.abort('Preselected stakeholders query cancelled');
  }, []);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // TinyMCE can re-serialize the loaded description slightly differently than the raw API value
  // (e.g. adding rel="noopener" to a link) purely on its own init, with no user interaction - strip
  // rel so that alone doesn't falsely enable "Save changes" right after opening the modal.
  const normalizeHtml = (html) => {
    if (!html) return html;
    const doc = new DOMParser().parseFromString(stripEmailAttachmentControls(html), 'text/html');
    doc.querySelectorAll('a[rel]').forEach((a) => a.removeAttribute('rel'));
    return doc.body.innerHTML;
  };

  const isFormValid = !!form.type && !!form.date;
  const isDirty =
    !isEditing ||
    (isBaselineReady &&
      (() => {
        const { description: prevDescription, ...prevRest } = initialFormRef.current ?? {};
        const { description: currentDescription, ...currentRest } = form;
        const restDiffers = JSON.stringify(prevRest) !== JSON.stringify(currentRest);
        const descriptionDiffers = normalizeHtml(prevDescription) !== normalizeHtml(currentDescription);
        return restDiffers || descriptionDiffers;
      })());

  const handleSubmit = async () => {
    if (!form.type) {
      createNotification('danger', 'Please select an interaction type');
      return;
    }
    if (!form.date) {
      createNotification('danger', 'Please select a date');
      return;
    }

    setIsSubmitting(true);
    const payload = {
      title: form.title,
      date: form.date,
      type: form.type,
      description: stripEmailAttachmentControls(form.description),
      attachments: form.attachments,
      tags: form.tags.map((tag) => tag.value),
      initiatedByOrganisation: form.initiator === 'Stakeholder' ? false : form.initiator ? true : null,
      initiator: form.initiator || null,
      contactIds: form.stakeholders.map((p) => p.id),
      userIds: form.members.map((m) => m.value),
    };
    try {
      const data = isEditing
        ? await saveCrmInteraction({ id: Number(interactionId), ...payload })
        : await addCrmInteraction(payload);
      createNotification('success', isEditing ? 'Interaction updated successfully' : 'Interaction logged successfully');
      onSuccess?.(data);
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalComponent
      isOpen={true}
      setIsOpen={(open) => {
        if (open) return;
        // This click's job was to close the dropdown (handled in onOverlayMouseDown below) - don't
        // also close the form under it.
        if (dropdownWasOpenRef.current) {
          dropdownWasOpenRef.current = false;
          return;
        }
        onClose();
      }}
      onOverlayMouseDown={() => {
        dropdownWasOpenRef.current = !!openDropdown;
        // The overlay's mousedown default action (the native focus-shift a field's dropdown relies
        // on to close via blur) ends up prevented further down this same handler chain - blur the
        // active field ourselves so its dropdown still closes.
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      }}
      maxWidth={700}
      height='90vh'
      notShowCloseButton={true}
    >
      <div className='d-flex flex-column h-100'>
        <div className='px-4 py-3 border-bottom flex-centered justify-content-between flex-shrink-0'>
          <h3 className='mb-0 title-h5-m-bold'>{modalTitle ?? (isEditing ? 'Edit interaction' : defaultTitle)}</h3>
          <button className='circle-button general-button hovered-button action-state-hover' onClick={onClose}>
            <span className='icon-close paragraph-p5' />
          </button>
        </div>

        <CustomScrollbar className='simple-scrollbar indented-scrollbar-small flex-grow-1' maximalThumbYSize={200}>
          <div className='px-4 py-4'>
            {isLoading ? (
              <InteractionFormFieldsSkeleton />
            ) : (
              <InteractionFormFields
                form={form}
                onChange={handleChange}
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
                recipientsSendRecords={recipientsSendRecords}
                isLoadingRecipients={isLoadingRecipients}
                senderEmailAddress={senderEmailAddress}
                emailSubject={emailSubject}
                onEditorReady={() => setIsEditorReady(true)}
              />
            )}
          </div>
        </CustomScrollbar>

        <div className='px-4 py-3 border-top flex-shrink-0 d-flex justify-content-end'>
          <button
            className='general-button border rounded-pill py-2 px-4 paragraph-p2 mr-3'
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className='general-button action-button rounded-pill py-2 px-4 paragraph-p2'
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading || !isFormValid || !isDirty}
          >
            {isSubmitting ? (isEditing ? 'Saving...' : 'Logging...') : isEditing ? 'Save changes' : 'Log interaction'}
          </button>
        </div>
      </div>
    </ModalComponent>
  );
};

export default LogInteractionModal;
